import { useCallback, useEffect, useState } from "react";

const DEFAULT_TTL_MS = 60 * 1000;

function resolveExpiresAtMs({ playExpiresAt, createdAt, ttlMs }) {
  if (playExpiresAt) return new Date(playExpiresAt).getTime();
  if (createdAt) return new Date(createdAt).getTime() + ttlMs;
  return null;
}

export function isPendingRoundActive(round, ttlMs = DEFAULT_TTL_MS, nowMs = Date.now()) {
  if (!round || round.status !== "Pending") return false;
  const expiresAtMs = resolveExpiresAtMs({
    playExpiresAt: round.playExpiresAt,
    createdAt: round.createdAt,
    ttlMs,
  });
  if (!expiresAtMs) return true;
  return expiresAtMs > nowMs;
}

export function usePendingPlayCountdown({
  active = false,
  playExpiresAt,
  createdAt,
  ttlMs = DEFAULT_TTL_MS,
  onExpired,
}) {
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [expired, setExpired] = useState(false);

  const handleExpired = useCallback(() => {
    setExpired(true);
    onExpired?.();
  }, [onExpired]);

  useEffect(() => {
    if (!active) {
      setSecondsLeft(null);
      setExpired(false);
      return undefined;
    }

    const expiresAtMs = resolveExpiresAtMs({ playExpiresAt, createdAt, ttlMs });
    if (!expiresAtMs) {
      setSecondsLeft(null);
      return undefined;
    }

    let fired = false;
    const tick = () => {
      const left = Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0 && !fired) {
        fired = true;
        handleExpired();
      }
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [active, playExpiresAt, createdAt, ttlMs, handleExpired]);

  return { secondsLeft, expired };
}

export function formatPlayCountdown(secondsLeft) {
  if (secondsLeft == null) return null;
  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}
